import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Download, Play, Ticket, ChevronRight, ArrowLeft, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader,
  WhatsAppBanner, NoireBanner 
} from '@/components/layout/MobileLayout';
import { mockTickets, mockEvents, getLivestreamStatus } from '@/lib/mockData';
import { formatDate, getTimeRemaining, cn } from '@/lib/utils';

// Countdown Timer
const CountdownTimer = ({ targetDate, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(targetDate));

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeRemaining(targetDate)), 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  if (timeLeft.total <= 0) {
    return (
      <span className="px-3 py-1 bg-red-600 text-white text-sm font-semibold rounded-full inline-flex items-center gap-1" data-testid="live-badge">
        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
        LIVE NOW
      </span>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-xs text-primary" data-testid="countdown-compact">
        <Clock className="w-3 h-3" />
        <span className="tabular-nums">{timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m</span>
      </div>
    );
  }

  return (
    <div className="text-center" data-testid="countdown-timer">
      <p className="text-xs text-muted-foreground mb-2">Event Starts In</p>
      <div className="flex items-center justify-center gap-2">
        {[
          { value: timeLeft.days, label: 'Days' },
          { value: timeLeft.hours, label: 'Hours' },
          { value: timeLeft.minutes, label: 'Mins' },
          { value: timeLeft.seconds, label: 'Secs' },
        ].map((item, i) => (
          <React.Fragment key={item.label}>
            {i > 0 && <span className="text-xl text-muted-foreground">:</span>}
            <div className="text-center">
              <span className="text-2xl font-bold text-white tabular-nums">{String(item.value).padStart(2, '0')}</span>
              <span className="text-xs text-primary block">{item.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// QR Code Display
const QRCodeDisplay = ({ code }) => (
  <div className="bg-white p-4 rounded-xl mx-auto max-w-[180px]" data-testid="qr-code">
    <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center relative overflow-hidden">
      <div className="grid grid-cols-8 gap-0.5 p-2">
        {Array.from({ length: 64 }).map((_, i) => (
          <div key={i} className={`w-2 h-2 rounded-sm ${(i * 7 + i % 3) % 2 === 0 ? 'bg-black' : 'bg-white'}`} />
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
          <span className="text-primary-foreground font-bold text-sm">88</span>
        </div>
      </div>
    </div>
    <p className="text-xs text-center text-gray-600 mt-2 font-mono">{code}</p>
  </div>
);

// Mobile Ticket Card
const MobileTicketCard = ({ ticket, onClick }) => {
  const isLivestreamTicket = ticket.ticketType === 'Livestream';
  const event = mockEvents.find(e => e.id === ticket.eventId);
  const livestreamStatus = event ? getLivestreamStatus(event.date, event.endDate) : 'UPCOMING';
  const isLive = livestreamStatus === 'LIVE';

  return (
    <div 
      className="bg-card border border-border rounded-xl overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
      data-testid={`ticket-card-${ticket.id}`}
    >
      <div className="flex gap-4 p-4">
        <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img src={ticket.eventImage} alt={ticket.eventTitle} className="w-full h-full object-cover" />
          {isLivestreamTicket && isLive && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded">LIVE</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm truncate">{ticket.eventTitle}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{formatDate(ticket.eventDate)}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={cn(
              "px-2 py-0.5 rounded text-xs font-medium",
              isLivestreamTicket ? "bg-primary/20 text-primary" : "bg-green-500/20 text-green-400"
            )}>
              {ticket.ticketType}
            </span>
            {isLivestreamTicket && !isLive && <CountdownTimer targetDate={ticket.eventDate} compact />}
          </div>
        </div>
        <div className="flex items-center">
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
      <div className="px-4 pb-4">
        <Button 
          className="w-full h-10 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground border border-primary font-medium"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          data-testid={`view-ticket-btn-${ticket.id}`}
        >
          VIEW TICKET
        </Button>
      </div>
    </div>
  );
};

// Desktop Ticket Card
const DesktopTicketCard = ({ ticket, isSelected, onClick }) => (
  <div 
    className={cn(
      "p-4 rounded-xl cursor-pointer transition-all",
      isSelected ? "bg-primary/15 border-2 border-primary" : "bg-card/80 border border-border hover:border-primary/50"
    )}
    onClick={onClick}
    data-testid={`ticket-card-${ticket.id}`}
  >
    <div className="flex gap-4">
      <img src={ticket.eventImage} alt={ticket.eventTitle} className="w-20 h-16 rounded-lg object-cover flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white truncate text-sm">{ticket.eventTitle}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Ticket Type: <span className="text-primary font-medium">{ticket.ticketType}</span>
        </p>
      </div>
    </div>
    <Button className="w-full mt-4 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground border border-primary h-9" size="sm" data-testid={`view-ticket-btn-${ticket.id}`}>
      VIEW TICKET
    </Button>
  </div>
);

// Mobile Ticket Details
const MobileTicketDetails = ({ ticket, onBack }) => {
  const navigate = useNavigate();
  const isLivestreamTicket = ticket.ticketType === 'Livestream';
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(ticket.eventDate));

  useEffect(() => {
    if (isLivestreamTicket) {
      const timer = setInterval(() => setTimeLeft(getTimeRemaining(ticket.eventDate)), 1000);
      return () => clearInterval(timer);
    }
  }, [ticket.eventDate, isLivestreamTicket]);

  const canJoinLivestream = isLivestreamTicket && timeLeft.total <= 0;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="ticket-details-mobile">
      {/* Header with back button */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center h-14 px-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-secondary rounded-lg" data-testid="back-button">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="ml-2 text-lg font-semibold text-white">Ticket Details</h1>
        </div>
      </header>

      <main className="pt-14 px-4">
        {/* Event Image */}
        <div className="relative h-48 -mx-4 mb-6">
          <img src={ticket.eventImage} alt={ticket.eventTitle} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-xl font-bold text-white">{ticket.eventTitle}</h2>
            <p className="text-sm text-muted-foreground mt-1">{formatDate(ticket.eventDate)}</p>
          </div>
        </div>

        {/* Ticket Type */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Ticket Type</span>
            <span className={cn(
              "px-3 py-1 rounded-full text-xs font-medium",
              isLivestreamTicket ? "bg-primary/20 text-primary" : "bg-green-500/20 text-green-400"
            )}>
              {ticket.ticketType}
            </span>
          </div>
          <span className="text-white text-sm font-medium">{ticket.ticketTypeName}</span>
        </div>

        {/* QR Code or Countdown */}
        <div className="py-4 mb-6" data-testid={isLivestreamTicket ? "livestream-section" : "qr-section"}>
          {isLivestreamTicket ? (
            <div className="space-y-4">
              <CountdownTimer targetDate={ticket.eventDate} />
              <p className="text-sm text-muted-foreground text-center max-w-sm mx-auto">
                You will be able to join using the &quot;Join Livestream&quot; button below once the event begins.
              </p>
            </div>
          ) : (
            <QRCodeDisplay code={ticket.qrCode} />
          )}
        </div>

        {/* Ticket Info */}
        <div className="bg-card border border-border rounded-xl p-4 space-y-0 mb-6" data-testid="ticket-info">
          <div className="flex justify-between py-3 border-b border-border/60">
            <span className="text-muted-foreground text-sm">Transaction ID</span>
            <span className="text-white font-mono text-sm">{ticket.transactionId}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-border/60">
            <span className="text-muted-foreground text-sm">Date Purchased</span>
            <span className="text-white text-sm">{formatDate(ticket.purchaseDate)}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-border/60">
            <span className="text-muted-foreground text-sm">Purchased By</span>
            <span className="text-white text-sm">{ticket.purchasedBy}</span>
          </div>
          <div className="flex justify-between py-3">
            <span className="text-muted-foreground text-sm">Quantity</span>
            <span className="text-white text-sm">{ticket.quantity} ticket(s)</span>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {isLivestreamTicket ? (
            <Button 
              className={cn(
                "w-full h-12 font-semibold",
                canJoinLivestream ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary/80 text-muted-foreground cursor-not-allowed"
              )}
              disabled={!canJoinLivestream}
              onClick={() => navigate(`/events/${ticket.eventId}/live`)}
              data-testid="join-livestream-btn"
            >
              <Play className="w-4 h-4 mr-2" fill={canJoinLivestream ? 'currentColor' : 'none'} />
              JOIN LIVESTREAM
            </Button>
          ) : (
            <Button className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="download-ticket-btn">
              <Download className="w-4 h-4 mr-2" />
              DOWNLOAD TICKET
            </Button>
          )}
          <Button 
            variant="outline" 
            className="w-full h-11 border-border text-white hover:bg-secondary"
            onClick={() => navigate(`/events/${ticket.eventId}`)}
            data-testid="view-event-btn"
          >
            VIEW EVENT
          </Button>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

// Desktop Ticket Details Panel
const DesktopTicketDetails = ({ ticket }) => {
  const navigate = useNavigate();
  const isLivestreamTicket = ticket.ticketType === 'Livestream';
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(ticket.eventDate));

  useEffect(() => {
    if (isLivestreamTicket) {
      const timer = setInterval(() => setTimeLeft(getTimeRemaining(ticket.eventDate)), 1000);
      return () => clearInterval(timer);
    }
  }, [ticket.eventDate, isLivestreamTicket]);

  const canJoinLivestream = isLivestreamTicket && timeLeft.total <= 0;

  return (
    <Card className="bg-card border-border border-dashed overflow-hidden" data-testid="ticket-details-panel">
      <div className="relative h-44">
        <img src={ticket.eventImage} alt={ticket.eventTitle} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
        <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{ticket.eventTitle}</h2>
            <p className="text-sm text-muted-foreground mt-1">{formatDate(ticket.eventDate)}</p>
          </div>
          <Button variant="outline" size="sm" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground" onClick={() => navigate(`/events/${ticket.eventId}`)}>
            VIEW EVENT
          </Button>
        </div>
      </div>
      <div className="p-5 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Ticket Type</span>
            <span className={cn("px-3 py-1 rounded-full text-xs font-medium", isLivestreamTicket ? "bg-primary/20 text-primary" : "bg-green-500/20 text-green-400")}>
              {ticket.ticketType}
            </span>
          </div>
          <span className="text-white text-sm font-medium">{ticket.ticketTypeName}</span>
        </div>
        <div className="py-2">
          {isLivestreamTicket ? (
            <div className="space-y-5">
              <CountdownTimer targetDate={ticket.eventDate} />
              <p className="text-sm text-muted-foreground text-center max-w-sm mx-auto">
                You will be able to join using the &quot;Join Livestream&quot; button below once the event begins.
              </p>
            </div>
          ) : (
            <QRCodeDisplay code={ticket.qrCode} />
          )}
        </div>
        <div className="space-y-0">
          <div className="flex justify-between py-3 border-b border-border/60"><span className="text-muted-foreground text-sm">Transaction ID</span><span className="text-white font-mono text-sm">{ticket.transactionId}</span></div>
          <div className="flex justify-between py-3 border-b border-border/60"><span className="text-muted-foreground text-sm">Date Purchased</span><span className="text-white text-sm">{formatDate(ticket.purchaseDate)}</span></div>
          <div className="flex justify-between py-3 border-b border-border/60"><span className="text-muted-foreground text-sm">Purchased By</span><span className="text-white text-sm">{ticket.purchasedBy}</span></div>
          <div className="flex justify-between py-3"><span className="text-muted-foreground text-sm">Quantity</span><span className="text-white text-sm">{ticket.quantity} ticket(s)</span></div>
        </div>
        <div className="space-y-3 pt-2">
          {isLivestreamTicket ? (
            <Button className={cn("w-full h-11 font-semibold", canJoinLivestream ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary/80 text-muted-foreground cursor-not-allowed")} disabled={!canJoinLivestream} onClick={() => navigate(`/events/${ticket.eventId}/live`)} data-testid="join-livestream-btn">
              <Play className="w-4 h-4 mr-2" fill={canJoinLivestream ? 'currentColor' : 'none'} />
              JOIN LIVESTREAM
            </Button>
          ) : (
            <Button className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="download-ticket-btn">
              <Download className="w-4 h-4 mr-2" />
              DOWNLOAD TICKET
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

// Main Component
export function UserMyTickets() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showMobileDetails, setShowMobileDetails] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (ticketId) {
      const ticket = mockTickets.find(t => t.id === ticketId);
      if (ticket) {
        setSelectedTicket(ticket);
        if (window.innerWidth < 1024) setShowMobileDetails(true);
      }
    } else if (mockTickets.length > 0 && window.innerWidth >= 1024 && !selectedTicket) {
      setSelectedTicket(mockTickets[0]);
    }
  }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    if (isMobile) setShowMobileDetails(true);
    navigate(`/me/tickets/${ticket.id}`, { replace: true });
  };

  const handleBackFromDetails = () => {
    setShowMobileDetails(false);
    navigate('/me/tickets', { replace: true });
  };

  // Mobile: Show ticket details
  if (isMobile && showMobileDetails && selectedTicket) {
    return <MobileTicketDetails ticket={selectedTicket} onBack={handleBackFromDetails} />;
  }

  // Mobile: Show ticket list
  if (isMobile) {
    return (
      <div className="min-h-screen bg-background pb-24" data-testid="my-tickets-mobile">
        <MobileHeader />
        <main className="pt-14 px-4">
          {/* WhatsApp Banner at top */}
          <div className="mt-4 mb-6">
            <WhatsAppBanner />
          </div>
          
          <h2 className="text-lg font-semibold text-white mb-4">My Tickets</h2>
          
          {mockTickets.length === 0 ? (
            <Card className="bg-card border-border p-8 text-center">
              <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">No Tickets Yet</h3>
              <p className="text-muted-foreground mb-4">Purchase tickets to see them here</p>
              <Link to="/"><Button className="bg-primary text-primary-foreground">Browse Events</Button></Link>
            </Card>
          ) : (
            <div className="space-y-4">
              {mockTickets.map(ticket => (
                <MobileTicketCard key={ticket.id} ticket={ticket} onClick={() => handleSelectTicket(ticket)} />
              ))}
            </div>
          )}
          
          {/* Noire Banner at bottom */}
          <div className="mt-6">
            <NoireBanner />
          </div>
        </main>
        <MobileBottomNav />
      </div>
    );
  }

  // Desktop: Master-detail layout
  return (
    <div className="min-h-screen bg-background" data-testid="my-tickets-desktop">
      <DesktopHeader />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* WhatsApp banner at top - Desktop (FULL WIDTH) */}
          <div className="mb-6">
            <WhatsAppBanner />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">My Tickets</h2>
              {mockTickets.length === 0 ? (
                <Card className="bg-card border-border p-8 text-center">
                  <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-white mb-2">No Tickets Yet</h3>
                  <p className="text-muted-foreground mb-4">Purchase tickets to see them here</p>
                  <Link to="/"><Button className="bg-primary text-primary-foreground">Browse Events</Button></Link>
                </Card>
              ) : (
                mockTickets.map(ticket => (
                  <DesktopTicketCard key={ticket.id} ticket={ticket} isSelected={selectedTicket?.id === ticket.id} onClick={() => handleSelectTicket(ticket)} />
                ))
              )}
            </div>
            <div className="lg:col-span-2">
              {selectedTicket ? <DesktopTicketDetails ticket={selectedTicket} /> : (
                <Card className="bg-card border-border p-12 text-center">
                  <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Select a Ticket</h3>
                  <p className="text-muted-foreground">Choose a ticket from the list to view details</p>
                </Card>
              )}
            </div>
          </div>

          {/* Noire banner at bottom - Desktop */}
          <div className="mt-8">
            <NoireBanner />
          </div>
        </div>
      </main>
    </div>
  );
}

export default UserMyTickets;
